'use client';

import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { BookingBookabilityReason, PrestationType } from '@cfixe-monorepo/shared';
import { useBooking } from '../../context/BookingContext';
import { type StepNav, useRegisterStepNav } from '../../context/StepNavContext';
import type { BookingPrestation, CartItem } from '../../types';
import { CategoryGroup } from '../shared/CategoryGroup';
import { SearchInput } from '../shared/SearchInput';
import { ServiceCard } from '../shared/ServiceCard';
import { RideServiceCard } from '../shared/RideServiceCard';
import { groupItemsByCategory } from '../../utils/category-grouping';
import { formatRelativeAge } from '../../utils/relative-age';

/* Noms entre guillemets français, comme les libellés de remplacement des cartes. */
const quoteNames = (names: string[]): string => names.map((name): string => `« ${name} »`).join(', ');

const SEARCH_THRESHOLD = 8;

export function ServiceSelection() {
  const { ctx, send, labels, headerAccessory, callbacks, locale, restoredSelection, answerRestoredSelection } =
    useBooking();
  const { prestations, categories } = ctx;
  const onSelectRidePrestation = callbacks.onSelectRidePrestation;

  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleConfirmServices = useCallback((): void => send({ type: 'CONFIRM_SERVICES' }), [send]);
  useRegisterStepNav(
    useMemo(
      (): StepNav => ({
        primary: {
          label: labels.continue,
          onClick: handleConfirmServices,
          disabled: ctx.cartItems.length === 0,
        },
      }),
      [labels.continue, handleConfirmServices, ctx.cartItems.length],
    ),
  );

  /* Cartes « Course » (type RIDE) : vitrine uniquement — leur CTA bascule vers le flux course via
     onSelectRidePrestation. DÉFENSIF : sans ce callback (hôte sans tunnel course), les items RIDE
     sont masqués plutôt que de rendre une carte sans action possible. */
  /* Prestations vitrine (ni prix ni durée) : rien à réserver, elles restent sur le site (spec Parcours § 7.8). */
  const visiblePrestations = useMemo(
    (): typeof prestations =>
      prestations.filter(
        (p): boolean =>
          p.bookability_reason !== BookingBookabilityReason.SHOWCASE &&
          (onSelectRidePrestation !== undefined || p.type !== PrestationType.RIDE),
      ),
    [prestations, onSelectRidePrestation],
  );

  const showSearch = visiblePrestations.length > SEARCH_THRESHOLD;

  const filtered = useMemo((): typeof prestations => {
    let result = visiblePrestations;

    if (activeCategoryId) {
      result = result.filter((p): boolean => p.category_id === activeCategoryId);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((p): boolean => p.name.toLowerCase().includes(q));
    }

    return result;
  }, [visiblePrestations, activeCategoryId, searchQuery]);

  const handleAddToCart = (item: CartItem): void => {
    send({ type: 'ADD_TO_CART', item });
    setExpandedId(null);
  };

  const handleReplaceCart = (item: CartItem): void => {
    send({ type: 'REPLACE_CART', item });
    setExpandedId(null);
  };

  const renderCard = (p: BookingPrestation): ReactNode =>
    p.type === PrestationType.RIDE && onSelectRidePrestation ? (
      <RideServiceCard key={p.prestation_id} prestation={p} onSelect={onSelectRidePrestation} />
    ) : (
      <ServiceCard
        key={p.prestation_id}
        prestation={p}
        isExpanded={expandedId === p.prestation_id}
        onToggle={() => setExpandedId(expandedId === p.prestation_id ? null : p.prestation_id)}
        onAdd={handleAddToCart}
        onReplace={handleReplaceCart}
      />
    );

  const renderServiceList = (): ReactNode => {
    /* If a category is selected or search is active, flat list */
    if (activeCategoryId || searchQuery.trim() || categories.length <= 1) {
      return filtered.map(renderCard);
    }

    /* Group by category (logique partagée avec le tunnel carte cadeau) */
    const nodes: ReactNode[] = [];
    for (const group of groupItemsByCategory(filtered, categories, (p): number | null | undefined => p.category_id)) {
      if (group.name) {
        nodes.push(
          <div key={`cat-${group.catalog_category_id}`} className="bt-service-group__title">
            {group.name}
          </div>,
        );
      }
      nodes.push(...group.items.map(renderCard));
    }
    return nodes;
  };

  return (
    <div className="bt-step">
      <div className={`bt-step-header ${headerAccessory ? 'bt-step-header--row' : ''}`}>
        <h2 className="bt-step-header__title">{labels.step_service}</h2>
        {headerAccessory ? <div className="bt-step-header__accessory">{headerAccessory}</div> : null}
      </div>

      {/* Panier d'une visite précédente : jamais repris sans réponse (spec Parcours § 7.8). */}
      {restoredSelection && (
        <div className="bt-banner bt-restored-selection" role="status">
          <p className="bt-restored-selection__text">
            <strong>
              {labels.restored_selection_text
                .replace('{names}', quoteNames(restoredSelection.names))
                .replace('{ago}', formatRelativeAge(restoredSelection.savedAt, Date.now(), locale))}
            </strong>
            {restoredSelection.kind === 'add' && (
              <span className="bt-restored-selection__question">
                {labels.restored_selection_add_question.replace('{names}', quoteNames(restoredSelection.currentNames))}
              </span>
            )}
          </p>
          <div className="bt-restored-selection__actions">
            <button type="button" className="bt-cta bt-cta--small" onClick={(): void => answerRestoredSelection(true)}>
              {restoredSelection.kind === 'add' ? labels.restored_selection_add : labels.restored_selection_resume}
            </button>
            <button
              type="button"
              className="bt-cta bt-cta--small bt-cta--secondary"
              onClick={(): void => answerRestoredSelection(false)}
            >
              {restoredSelection.kind === 'add' ? labels.restored_selection_replace : labels.restored_selection_restart}
            </button>
          </div>
        </div>
      )}

      {/* Formule choisie en amont (site vitrine) devenue non réservable : le tunnel ne lui a PAS
          substitué une autre ligne de prix — il l'a écartée et le dit ici, plutôt que de laisser
          le client payer un autre montant que celui qu'il a choisi. */}
      {ctx.droppedPreselections.length > 0 && (
        <div className="bt-banner bt-banner--warning">
          {labels.preselect_unavailable.replace('{names}', ctx.droppedPreselections.join(', '))}
        </div>
      )}
      {ctx.unbookablePreselections.length > 0 && (
        <div className="bt-banner bt-banner--warning">
          {labels.preselect_not_bookable.replace('{names}', ctx.unbookablePreselections.join(', '))}
        </div>
      )}

      {showSearch && (
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={labels.search_placeholder}
          clearLabel={labels.clear_search}
        />
      )}

      <CategoryGroup
        categories={categories}
        activeCategoryId={activeCategoryId}
        onChange={setActiveCategoryId}
        allCategoriesLabel={labels.all_categories}
        scrollPrevLabel={labels.categories_scroll_prev}
        scrollNextLabel={labels.categories_scroll_next}
      />

      <div className="bt-service-list">{renderServiceList()}</div>
    </div>
  );
}
